import { useDispatch, useSelector } from "react-redux";
import { Accordion, AccordionDetails, AccordionSummary, Button, CircularProgress, IconButton, Link, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";

import { AccessTime, Cancel, CloudUpload, Download, ExpandMore, PictureAsPdf, Share } from "@mui/icons-material";
import { selectFBA } from "../../reducers/fbaSlice";
import { FBAInputChart } from "./FBAInputChart";
import { FBAProductionDataTable } from "./FBAProductionDataTable";
import { FBADiagnosticChart } from "./FBADiagnosticChart";
import { selectOptions, setFbaTabItem, setIsLoadingData, setShowSidePanel } from "../../reducers/optionsSlice";
import { FBARTAChart } from "./FBARTAChart";
import { FBADiagnosticDataTable } from "./FBADiagnosticDataTable";

import { FBAHistoryMatchChart } from "./FBAHistoryMatchChart";
import { FBASummary } from "./FBASummary";
import { FBAHistoryMatchDataTable } from "./FBAHistoryMatchDataTable";
import { FBARTASummary } from "./FBARTASummary";
import { FBAHistoryMatchSummary } from "./FBAHistoryMatchSummary";
import { FBAWellboreModelChart } from "./FBAWellboreModelChart";
import { FBAWellboreModelDataTable } from "./FBAWellboreModelDataTable";
import { selectUser } from "../../reducers/userSlice";
import { Box } from "@mui/system";
import { useSnackbar } from "notistack";
import { useState } from "react";

import { addProject, selectProjects } from "../../reducers/projectSlice";
import { selectLists } from "../../reducers/listSlice";
import { selectWells } from "../../reducers/wellSlice";
import { saveEconomics, selectEconomics } from "../../reducers/economicsSlice";
import { ShareFBA } from "./ShareFBA";
import { fbaConversionFactors } from "./fbaConversionFactors";
import { useSocket } from "../../context/useSocket";
import { FBAAnalyticsChart } from "./FBAAnalyticsChart";
import { FBAAnalytics } from "./FBAAnalytics";


const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.grey[100],
    border: 0,
    color: 'text.primary',
    overflowY: 'scroll',
    flexBasis: 0,
    flexShrink: 1,
    flexGrow: 1,
    padding: 0,
    paddingTop: 10,
    margin: 0,
    marginTop: 10,
  },
  header: {
    padding: '0 10px',
    paddingTop: 10,
    position: 'relative'
  },
  cards: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  card: {
    margin: '0 5px',
    height: 120,
    padding: 10,
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 'auto',
    maxWidth: 300,
  },
  status: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: 10
  },
  icon: {
    color: theme.palette.text.secondary
  },
  summaryContainer: {
    display: 'flex',
    margin: '0 20px',
    justifyContent: 'space-around'
  },
  summarySection: {
    display: 'flex',
    flexDirection: 'column',
    // marginRight: 60
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  summaryHeading: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: theme.palette.text.secondary
  },
  summaryText: {
    fontSize: '12px'
  },
  summaryValue: {
    paddingLeft: 10
  },
}));

export const FBA = ({fbaUnits, setFBAUnits}) => {

  const theme = useTheme();
  const classes = useStyles();
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const options = useSelector(selectOptions);
  const fba = useSelector(selectFBA);
  const currentFBA = fba?.byId?.[fba?.currentFBAId];
  const projects = useSelector(selectProjects);
  const currentProject = projects.byId[projects.currentProjectId];
  const lists = useSelector(selectLists);
  const wells = useSelector(selectWells);
  const economics = useSelector(selectEconomics);

  const darkMode = user?.value?.darkMode;
  const demoUser = user?.value?.subscription === 'Demo';

  const { socket } = useSocket();

  // const units = currentFBA?.settings?.units?.value;

  const { enqueueSnackbar } = useSnackbar();

  const [shareFBAVisible, setShareFBAVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  try {
    return (
      <div className={classes.root} style={{backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light']}} id="fba">
        {shareFBAVisible && <ShareFBA
          darkMode={darkMode} 
          setShareFBAVisible={setShareFBAVisible} 
          handleExportFBAJSONClick={handleExportFBAJSONClick} 
          handleCopyFBALink={handleCopyFBALink}
        />}

        <Box style={{
          boxShadow: `0px 2px 6px ${darkMode ? '#222' : 'lightgrey'}`,
          backgroundColor: darkMode ? theme?.palette?.elevationOne?.dark : theme?.palette?.elevationTwo?.light
        }}>
          <div style={{display: 'flex', width: '100%', justifyContent: 'space-between', position: 'relative'}}>
            <div className={classes.header}>
              <div style={{display: 'flex', flexDirection: 'column'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', textOverflow: 'ellipsis'}}>
                  <Typography variant="h6" style={{fontSize: 16}} color="textSecondary">
                    {getTabName(options?.fbaTabItem)} Report
                  </Typography>
                </div>
                <Typography variant="h4" noWrap style={{marginTop: 25, overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 600}}>
                  {currentFBA?.name || ""}
                </Typography>
              </div>
            </div>

            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', position: 'absolute', top: 10, right: 10}}>
              
              <div style={{display: 'flex', flexDirection: 'row'}}>
                <Link href="https://ssdprojects.s3.us-east-2.amazonaws.com/Athena+FBA+User+Manual_v3.pdf"
                  target="_blank" style={{textDecoration: 'none', height: 30}}>
                  <Tooltip title="Download FBA manual">
                    <Button 
                      style={{height: 30, color: darkMode ? '#fff' : theme.palette.primary.main, borderColor: darkMode ? '#fff' : theme.palette.primary.main, marginTop: 0}} 
                      startIcon={<PictureAsPdf />} 
                      color={darkMode ? "inherit" : "primary"}
                      variant="outlined"
                      size="small"
                    >
                      User Manual
                    </Button>
                  </Tooltip>
                </Link>
                <Tooltip title="Share FBA">
                  <Button 
                    startIcon={<Share />} 
                    onClick={() => setShareFBAVisible(true)}
                    variant="outlined"
                    size="small"
                    style={{height: 30, marginRight: 10, marginLeft: 10, color: darkMode ? '#fff' : theme.palette.primary.main, borderColor: darkMode ? '#fff' : theme.palette.primary.main}}
                    color={darkMode ? "inherit" : "primary"}
                  >
                    Share
                  </Button>
                </Tooltip>
                <div style={{position: 'relative'}}>
                  <Tooltip title="Save Project">
                    <span><Button 
                      startIcon={<CloudUpload />} 
                      size="small"
                      variant="contained" 
                      style={{color: darkMode ? '#000' : '#fff', height: 30}}
                      color={darkMode ? "inherit" : "primary"}
                      edge="end" 
                      onClick={() => handleSaveProject(currentProject)}
                      disabled={demoUser || isLoading || user?.value?.email !== projects?.byId[projects?.currentProjectId]?.createdBy}
                    >
                      Save Project
                    </Button></span>              
                  </Tooltip>

                  {isLoading && (
                    <CircularProgress
                      size={24}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-11px',
                        marginLeft: '-12px',
                      }}
                    />
                  )}
                </div>
              </div>

              <div style={{display: 'flex', alignItems: 'center', marginTop: 5}}>
                <AccessTime color="textSecondary" fontSize="14px" style={{ marginRight: 5 }} />
                <Typography color="textSecondary" fontSize="14px" textAlign={'right'}>Last saved: {new Date(currentProject?.lastModified)?.toLocaleString()}</Typography>
              </div>
            
            </div>

          </div>
          <Tabs
            className={classes.tabs}
            value={options?.fbaTabItem}
            TabIndicatorProps={{style: {background: darkMode ? '#fff' : theme.palette.primary.main}}}
            textColor={darkMode ? "inherit" : "primary"}
            onChange={(e, value) => handleTabItemChange(value)}
            style={{
              backgroundColor: (darkMode ? theme?.palette?.elevationOne?.dark : theme?.palette?.elevationTwo?.light),
              borderBottom: `1px solid ${darkMode ? 'grey' : 'lightgrey'}`,
            }}
          >
            <Tab label={getTabName(0)} disabled={!currentFBA.fbaData} />
            <Tab label={getTabName(1)} disabled={!currentFBA.fbaWellboreModel} />
            <Tab label={getTabName(2)} disabled={!currentFBA.diagnostics} />
            <Tab label={getTabName(3)} disabled={!currentFBA.rtaSummary} />
            <Tab label={getTabName(4)} disabled={!currentFBA.historyMatch} />
            <Tab label={getTabName(5)} disabled={!currentFBA?.rtaSummary || !currentFBA?.historyMatch} />
            <Tab label={getTabName(6)} disabled={!currentFBA?.fbaData} />
          </Tabs>

        </Box>

        {options?.fbaTabItem === 5 ? 
        <FBASummary fba={currentFBA} darkMode={darkMode} units={fbaUnits} /> :
        options?.fbaTabItem === 6 ? 
        <FBAAnalytics fba={fba} darkMode={darkMode} fbaUnits={fbaUnits} /> :
        (<>
           {(options?.fbaTabItem === 3 || options?.fbaTabItem === 4) && <Accordion 
            defaultExpanded 
            sx={{boxShadow: 1}}
            style={{
              margin: 10,
              borderRadius: 4,
              border: 'none',
              backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
            }}
            elevation={0}
          >
            <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none'}}>
              <Typography className={classes.heading} noWrap>{getTabName(options?.fbaTabItem)} Summary</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              {options?.fbaTabItem === 3 ? <FBARTASummary fba={currentFBA} /> : <FBAHistoryMatchSummary fba={currentFBA} />}
            </AccordionDetails>
          </Accordion>}
          

          {options?.fbaTabItem === 1 && 
          <Button 
            startIcon={<Download />} 
            style={{marginTop: 10, marginLeft: 10, color: (darkMode ? '#fff' : theme.palette.primary.main), borderColor: (darkMode ? '#fff' : theme.palette.primary.main)}}
            color={(darkMode ? "inherit" : "primary")}
            variant="outlined"
            size="small"
            onClick={handleDownloadWellboreModelClick}
          >
            Download CSV
          </Button>}


          <Accordion 
            defaultExpanded 
            sx={{boxShadow: 1}}
            style={{
              margin: 10,
              borderRadius: 4,
              border: 'none',
              backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
              position: 'relative'
            }}
            elevation={0}
          >
            <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none'}}>
              <Typography className={classes.heading} noWrap>{getTabName(options?.fbaTabItem)} Charts</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              {renderChart(options?.fbaTabItem, fbaUnits)}
            </AccordionDetails>
          </Accordion>

          {(options?.fbaTabItem === 0) && 
          <Accordion 
            defaultExpanded
            sx={{boxShadow: 1}}
            style={{
              margin: 10,
              borderRadius: 4,
              border: 'none',
              backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
            }}
            elevation={0}
          >
            <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none'}}>
              <Typography className={classes.heading} noWrap>{getTabName(options?.fbaTabItem)} Table</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              {renderTable(options?.fbaTabItem, fbaUnits)}
            </AccordionDetails>
          </Accordion>}
        </>)
        }
        
        {options?.isLoadingData && 
          <div style={{backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.75)', position: 'absolute', left: 0, top: 0, zIndex: 420, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%'}}>
            <Typography style={{marginBottom: 20}}>Calculating FBA...</Typography>
            <CircularProgress></CircularProgress>
            <IconButton style={{position: 'absolute', bottom: '60px'}} onClick={() => {
              dispatch(setIsLoadingData(false));
              if (socket && socket.readyState === 1) {
                socket.send(JSON.stringify({
                  messageType: 'killall', userEmail: user?.value?.email
                }));
              };
            }}><Cancel color="grey" /> </IconButton>
        </div>}
      </div>
    );

    function handleTabItemChange(tabItem) {
      if (!options?.showSidePanel) {
        dispatch(setShowSidePanel(true));
      };
      dispatch(setFbaTabItem(tabItem));
    };

    function getTabName(tabItem) {
      switch(tabItem) {
        case 0:
          return 'Input Data';
        case 1:
          return 'Wellbore Model';
        case 2:
          return 'Diagnostics';
        case 3:
          return 'FracSeed';
        case 4:
          return 'FracSolve';
        case 5:
          return 'Summary';
        case 6:
          return 'Analytics';
        default:
          return '';
      };
    };

    function renderChart(tabItem, fbaUnits) {
      switch(tabItem) {
        case 0:
          return <FBAInputChart />;
        case 1:
          return <FBAWellboreModelChart />;
        case 2:
          return <FBADiagnosticChart />;
        case 3:
          return <FBARTAChart />;
        case 4:
          return <FBAHistoryMatchChart fbaUnits={fbaUnits} />;
        case 6:
          return <FBAAnalyticsChart />;
        default:
          return '';
      };
    };

    function renderTable(tabItem, units) {
      switch(tabItem) {
        case 0:
          return <FBAProductionDataTable fbaData={currentFBA?.fbaData} units={currentFBA?.settings?.units?.value || "field"} />;
        case 1:
          return <FBAWellboreModelDataTable fbaData={currentFBA?.fbaData} fbaWellboreModel={currentFBA?.fbaWellboreModel} />;
        case 2:
          return <FBADiagnosticDataTable fbaData={currentFBA?.fbaData} fbaDiagnostics={currentFBA?.diagnostics} />;
        case 3:
          return <></>;
          // return <FBARTADataTable 
          //   fbaData={currentFBA?.fbaData} 
          //   fbaDiagnostics={currentFBA?.diagnostics} 
          //   fbaRta={currentFBA?.fmb}
          //   fbaLinearFlowTypeCurve={currentFBA?.linearFlowTypeCurve}
          // />;
        case 4:
          return <FBAHistoryMatchDataTable fba={currentFBA} units={fbaUnits} />;
        default:
          return <></>;
      };
    };

    function handleCopyFBALink() {
      try {
        if (!currentFBA?.id) return;
        // let copyText = currentFBA?.location;
        let copyText = `https://ssd-fbas.s3.us-east-2.amazonaws.com/${currentFBA?.id}.json`;
        if (!copyText) return;
        navigator.clipboard.writeText(copyText);
      
        enqueueSnackbar('FBA link copied to clipboard', {
          variant: 'info'
        });

        setShareFBAVisible(false);
      } catch (error) {
        console.log(error);
      };
    };

    function handleExportFBAJSONClick() {
      try {
        let dataStr = JSON.stringify(currentFBA);
        if (!dataStr?.length > 0) return;

        let dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        const date = new Date().toLocaleDateString();
        let exportFileDefaultName = `${currentFBA?.name}_${date}.json`;
        let linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();

        setShareFBAVisible(false);
      } catch (error) {
        console.log(error);
        return;
      };
    };

    function handleSaveProject(project) {
      try {
        if (!project) return;

        console.log('Saving project ' + project.name);
        
        setIsLoading(true);

        let newWells = project?.wells?.length > 0 ? [...project.wells] : [];

        // save all project economics
        const economicsIds = economics?.allIds?.filter(id => economics?.byId?.[id]?.projectId === currentProject?.id); // only keep economicsIds that correspond to the current project
        if (economicsIds?.length > 0) {
          for (let economicsId of economicsIds) {
            if (economics?.byId?.[economicsId]?.forecastNet && economics?.byId?.[economicsId]?.cashflowNet && economics?.byId?.[economicsId]?.cashflowGross) {
              dispatch(saveEconomics(economics?.byId?.[economicsId]));
            };
          };
        };

        // save all project fbas
        const fbaIds = fba?.allIds?.filter(id => fba?.byId?.[id]?.projectId === currentProject?.id); // only keep economicsIds that correspond to the current project
        if (fbaIds?.length > 0 && socket && socket.readyState === 1) {
          for (let fbaId of fbaIds) {
            const currFBA = fba?.byId?.[fbaId];
            if (currFBA?.fbaData) {

              socket.send(JSON.stringify({
                messageType: 'saveFBA', 
                fba: currFBA, 
                fileName: currFBA?.id, 
                userEmail: user?.value?.email
              }));

            };
          };
        };
  
        // compile a list of UWIs in the project
        let projectUwis = [];
  
        project.listIds.forEach(listId => {
  
          lists.byId[listId]?.wellUwis?.forEach(wellUwi => {
            projectUwis.push(wellUwi);
          });
  
        });
  
        for (let wellUwi of projectUwis) { // save local params
          try {
            if (wells?.byUwi?.[wellUwi]?.status === 'processed' && wells?.byUwi?.[wellUwi]?.params && !wells?.byUwi?.[wellUwi]?.local) { // if params are available, well is processed
              let wellParams = {...wells?.byUwi?.[wellUwi]?.params};
              const wellFluids = Object.keys(wellParams)?.filter(f => f !== 'cutoff');
      
              for (let fluid of wellFluids) {
                wellParams[fluid] = {...wellParams?.[fluid], outliers: []};
              };
  
              const wellIndex = newWells.map(w => w.uwi)?.indexOf(wellUwi);
              if (wellIndex === -1) { // if well hasnt been saved before, add it
                newWells.push({uwi: wellUwi, params: wellParams});
              } else { // update existing saved well with new params
                newWells.splice(wellIndex, 1, {uwi: wellUwi, params: wellParams});
              };
            };
            
          } catch (error) {
            console.log(error);
          };
        };
  
        const newProject = {
          id: project?.id,
          name: project?.name, 
          color: project?.color,
          description: project?.description || "",
          createdBy: project?.createdBy,
          lastModified: new Date().toLocaleString(),
          createdOn: project?.createdOn,
          listIds: project?.listIds || [],
          wells: newWells,
          currentListId: project?.currentListId || "",
          economicsIds: economicsIds?.length > 0 ? economicsIds : (project?.economicsIds || []),
          fbaIds: fbaIds?.length > 0 ? fbaIds : (project?.fbaIds || []),
          public: project?.public || false
        };
  
        const newLists = project?.listIds?.length > 0 ? project?.listIds?.map(listId => {
          const currentList = lists.byId[listId];
          return {
            id: listId,
            name: currentList?.name,
            createdBy: currentList?.createdBy,
            lastModified: new Date().toLocaleString(),
            color: currentList?.color,
            description: currentList?.description || "",
            wellUwis: currentList?.wellUwis.filter(uwi => !wells.byUwi[uwi]?.local),
            typeCurveId: currentList?.typeCurveId,
            groupWellId: currentList?.groupWellId,
            forecastListTab: currentList?.forecastListTab,
            filters: currentList?.filters
          };
        }) : [];

        dispatch(addProject({ project: newProject, lists: newLists, email: user?.value?.email })).then(() => {
          setIsLoading(false);
          enqueueSnackbar(`Project ${project?.name} saved successfully`, { variant: 'success' });
        });
        
      } catch (error) {
        console.log(error);
        enqueueSnackbar(`Could not save project ${project?.name}`, { variant: 'error' });
        return;
      };
      
    };

    function handleDownloadWellboreModelClick() {
      try {      
        console.log('Downloading wellbore model CSV...');

        if (!currentFBA?.fbaData) return;

        const headers=['Date', 'Time (days)', `Pwh (${fbaUnits === 'metric' ? 'kPa' : 'psia'})`, `Pwf (${fbaUnits === 'metric' ? 'kPa' : 'psia'})`];
        let csv = "";
        csv += headers.join(",") + "\r\n";

        for (let d of currentFBA?.fbaData) {
          csv += `${d?.date},${d?.days},${fbaUnits === 'metric' ? d?.pwh * fbaConversionFactors?.pwh : d?.pwh},${fbaUnits === 'metric' ? d?.pwf * fbaConversionFactors?.pwf : d?.pwf}\r\n`;
        };

        const hiddenElement = document.createElement('a');  
        hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);  
        hiddenElement.target = '_blank';  
        hiddenElement.download = `${currentFBA?.name}_wellbore_model_${fbaUnits}.csv`;  
        hiddenElement.click();  

        return;

      } catch (error) {
        console.log(error);
      };
    };

  } catch (error) {
    console.log(error);
    return (<div style={{width: '100%'}}></div>);
  };
};
